<template>
  <MembershipPage
    title="You're on a style break">
    <template #aboveBox>
      <div class="d-flex flex-column gap-sm">
        <h6 v-if="pauseResumeDate">
          Your subscription will automatically resume on {{ formattedPauseResumeDate }}.
        </h6>
        <p>We'll remind you by email a few days before your subscription restarts. If you're ready to return sooner, you can restart your membership anytime.</p>
        <div class="d-flex flex-column gap-sm">
          <p v-if="isExtensionDateEligible && !eligibleForPause && ineligibleForPauseReason === 'ITEMS_OUT'">
            To extend your pause, please return or purchase any outstanding items first.
          </p>
          <p v-else-if="isExtensionDateEligible && !eligibleForPause">
            If you need to skip another month, please email us at <a href="mailto:hi@armoire.style?subject=I%20have%20a%20question">
              hi@armoire.style
            </a>
          </p>
        </div>
      </div>
    </template>
    <template #boxContent>
      <div class="d-flex flex-column gap-xl">
        <div
          v-if="canExtendPause"
          class="d-flex flex-column gap-sm align-items-center">
          <b>Want to keep your membership paused?</b>
          <p>You can extend your pause by one month.</p>
          <BaseButton
            text="Extend Pause"
            :disabled="buttonDisabled"
            @click="onExtendPauseClick"/>
        </div>
        <div
          v-else-if="!isExtensionDateEligible && extensionEligibleDate"
          class="d-flex flex-column gap-sm align-items-center">
          <p>
            You can extend your pause starting on {{ formattedExtensionEligibleDate }}.
          </p>
          <BaseButton
            :disabled="true"
            text="Extend Pause"/>
        </div>
        <div class="d-flex flex-column gap-sm align-items-center">
          <b>Ready to return?</b>
          <p>You can resume your membership anytime.</p>
          <BaseButton
            :to="{ name: 'resume' }"
            text="Resume Membership"/>
        </div>
      </div>
    </template>
    <template #belowBox>
      <BaseButton
        class="align-self-center"
        variant="text-link"
        text="Go to My Account"
        :to="{ name: 'account' }"/>
    </template>
  </MembershipPage>
</template>

<script setup>
import MembershipPage from '@/components/account/membership/new/MembershipPage.vue'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAccountStore } from '@/stores/account.js'
import { usePauseCancel } from '@/composables/pauseCancel.js'
import BaseButton from '@shared/components/ADORN/BaseButton.vue'
import useAnalytics from '@shared/composables/analytics.js'

const router = useRouter()
const accountStore = useAccountStore()
const { pauseSubscription } = accountStore
const {
  getBilling,
  clientIsPaused,
  eligibleForPause,
  ineligibleForPauseReason,
  pauseResumeDate,
  formattedPauseResumeDate,
  extensionEligibleDate,
  formattedExtensionEligibleDate,
  isExtensionDateEligible,
  canExtendPause
} = usePauseCancel()
const { trackPageViewEvent } = useAnalytics()

const buttonDisabled = ref(false)

const onExtendPauseClick = async () => {
  buttonDisabled.value = true
  try {
    await pauseSubscription({ months: 1 })
    await getBilling()
    router.push({ name: 'pause', query: { extendPause: true } })
  } finally {
    buttonDisabled.value = false
  }
}
onMounted(async () => {
  trackPageViewEvent('Extend Pause')
  await getBilling()
  if (!clientIsPaused.value) {
    router.push({ name: 'pause', query: { extendPause: true } })
  }
})
</script>

